/**
 * Error handling utilities for contract signing
 */

import type {
  ErrorType,
  ErrorConfig,
  ErrorResponseData,
  ContractSigningError,
  HttpStatusCode,
} from "@/types/contractSigning";

/**
 * Error configuration map for status codes
 */
export const ERROR_CONFIG_MAP: Record<HttpStatusCode | "default", ErrorConfig> = {
  404: {
    errorType: "invalid",
    i18nKey: "contractSigning.errorInvalidHash",
    i18nDescriptionKey: "contractSigning.errorInvalidHashDescription",
    snackbarType: "Error",
  },
  403: {
    errorType: "alreadyDeclined",
    i18nKey: "contractSigning.errorAlreadyDeclined",
    i18nDescriptionKey: "contractSigning.errorAlreadyDeclinedDescription",
    snackbarType: "Error",
  },
  410: {
    errorType: "expired",
    i18nKey: "contractSigning.errorExpired",
    i18nDescriptionKey: "contractSigning.errorExpiredDescription",
    snackbarType: "Error",
  },
  409: {
    errorType: "alreadyAccepted",
    i18nKey: "contractSigning.errorAlreadyAccepted",
    i18nDescriptionKey: "contractSigning.errorAlreadyAcceptedDescription",
    snackbarType: "Warning",
  },
  500: {
    errorType: "network",
    i18nKey: "contractSigning.errorNetwork",
    i18nDescriptionKey: "contractSigning.errorNetworkDescription",
    snackbarType: "Error",
  },
  default: {
    errorType: "network",
    i18nKey: "contractSigning.errorNetwork",
    i18nDescriptionKey: "contractSigning.errorNetworkDescription",
    snackbarType: "Error",
  },
};

/**
 * PDF error configuration
 */
export const PDF_ERROR_CONFIG: ErrorConfig = {
  errorType: "pdf",
  i18nKey: "contractSigning.errorLoadingPdf",
  i18nDescriptionKey: "contractSigning.errorLoadingPdfDescription",
  snackbarType: "Error",
};

/**
 * Get error type from HTTP status code
 * @param status - HTTP status code
 * @returns Error type
 */
export function getErrorTypeFromStatus(status: number | undefined): ErrorType {
  if (!status) {
    return "network";
  }

  const config = ERROR_CONFIG_MAP[status as HttpStatusCode] || ERROR_CONFIG_MAP.default;
  return config.errorType;
}

/**
 * Get error configuration from HTTP status code
 * @param status - HTTP status code
 * @returns Error configuration
 */
export function getErrorConfigFromStatus(status: number | undefined): ErrorConfig {
  if (!status) {
    return ERROR_CONFIG_MAP.default;
  }

  return ERROR_CONFIG_MAP[status as HttpStatusCode] || ERROR_CONFIG_MAP.default;
}

/**
 * Parse error response from blob
 * @param blob - Error response blob
 * @returns Parsed error response data
 */
export async function parseErrorResponse(blob: Blob): Promise<ErrorResponseData> {
  try {
    const errorText = await blob.text();
    return JSON.parse(errorText) as ErrorResponseData;
  } catch (parseError) {
    return { error: "Failed to parse error response" };
  }
}

/**
 * Create a contract signing error from an error object
 * @param error - The error to convert
 * @param status - Optional HTTP status code
 * @param responseData - Optional response data
 * @returns Contract signing error
 */
export function createContractSigningError(
  error: Error | unknown,
  status?: HttpStatusCode,
  responseData?: ErrorResponseData
): ContractSigningError {
  const baseError = error instanceof Error ? error : new Error(String(error));
  const contractError = baseError as ContractSigningError;

  if (status !== undefined) {
    contractError.status = status;
  }

  if (responseData) {
    contractError.responseData = responseData;
  }

  return contractError;
}

